import type { GridApi } from "ag-grid-community";
import type { Ref } from "vue";
import { shallowRef } from "vue";

import { isSameOrder } from "./adapterRowGroups";

export interface RowGroupChip {
  colId: string;
  label: string;
}

export const CHIP_SELECTOR = ".ag-column-drop-cell:not(.ag-column-drop-cell-ghost)";
export const OVERFLOWED_CHIP_CLASS = "sca-row-group-chip-overflowed";

/**
 * Chips that do not fit the chips' box move into a "⋯" list placed right after the last chip that
 * fits; when none fits, the menu lists them all instead. The box takes its width from the panel,
 * never from the chips, so it grows back as soon as the toolbar has room again.
 */
export function useRowGroupPanelOverflow(gridApi: Ref<GridApi | null>) {
  const host = shallowRef<HTMLElement | null>(null);
  /** Every grouping column, in grouping order. */
  const chips = shallowRef<RowGroupChip[]>([]);
  const hiddenChips = shallowRef<RowGroupChip[]>([]);
  let panel: HTMLElement | null = null;
  let frame = 0;

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(measure);
  };
  const resizeObserver = new ResizeObserver(schedule);
  // AG Grid rebuilds the chips whenever the grouping columns change.
  const mutationObserver = new MutationObserver(schedule);

  function measure() {
    frame = 0;
    const box = panel?.querySelector<HTMLElement>(".ag-column-drop-horizontal");
    const api = gridApi.value;
    if (!panel || !box || !host.value || !api || api.isDestroyed()) return;

    const chipEls = [...box.querySelectorAll<HTMLElement>(CHIP_SELECTOR)];
    const panelLeft = panel.getBoundingClientRect().left;
    const rightOf = (el: HTMLElement) => el.getBoundingClientRect().right - panelLeft;
    const limit = rightOf(box);
    const gap = parseFloat(getComputedStyle(box.querySelector(".ag-column-drop-list") ?? box).columnGap) || 0;

    const lastChip = chipEls.at(-1);
    let visibleCount = chipEls.length;
    if (lastChip && rightOf(lastChip) > limit) {
      const room = limit - gap - host.value.offsetWidth;
      visibleCount = chipEls.findIndex((chip) => rightOf(chip) > room);
    }

    chipEls.forEach((chip, index) => chip.classList.toggle(OVERFLOWED_CHIP_CLASS, index >= visibleCount));
    const lastVisible = chipEls[visibleCount - 1];
    host.value.style.left = `${lastVisible ? rightOf(lastVisible) + gap : box.offsetLeft}px`;

    const all = api
      .getRowGroupColumns()
      .map((col) => ({ colId: col.getColId(), label: api.getDisplayNameForColumn(col, "header") ?? col.getColId() }));
    setIfChanged(chips, all);
    setIfChanged(hiddenChips, all.slice(visibleCount));
  }

  function attach(el: HTMLElement) {
    detach();
    panel = el;
    const overflowHost = document.createElement("div");
    overflowHost.className = "sca-row-group-overflow-host";
    panel.append(overflowHost);
    host.value = overflowHost;
    resizeObserver.observe(panel);
    mutationObserver.observe(panel, { childList: true, subtree: true });
    schedule();
  }

  function detach() {
    cancelAnimationFrame(frame);
    frame = 0;
    resizeObserver.disconnect();
    mutationObserver.disconnect();
    host.value?.remove();
    host.value = null;
    chips.value = [];
    hiddenChips.value = [];
    panel = null;
  }

  return { host, chips, hiddenChips, attach, detach, remeasure: schedule };
}

const isSameChip = (a: RowGroupChip, b: RowGroupChip) => a.colId === b.colId && a.label === b.label;

function setIfChanged(target: Ref<RowGroupChip[]>, next: RowGroupChip[]) {
  if (!isSameOrder(target.value, next, isSameChip)) target.value = next;
}
